import { useEffect } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import logo from "@/assets/oz-move-logo.webp";
import Footer from "@/components/Footer";

const PrivacyPolicy = () => {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content") ?? "";
    const prevOgTitle = document.querySelector('meta[property="og:title"]')?.getAttribute("content") ?? "";
    const prevOgDesc = document.querySelector('meta[property="og:description"]')?.getAttribute("content") ?? "";
    const prevOgUrl = document.querySelector('meta[property="og:url"]')?.getAttribute("content") ?? "";
    const prevCanonical = document.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? "";

    document.title = "Privacy Policy | OZ MOVE CO.";
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      "OZ MOVE CO. Privacy Policy. Learn how we collect, use, and protect your personal information in line with the Australian Privacy Act 1988."
    );
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", "Privacy Policy | OZ MOVE CO.");
    document.querySelector('meta[property="og:description"]')?.setAttribute(
      "content",
      "Learn how OZ MOVE CO. handles your personal information."
    );
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", "https://ozmoveco.com.au/privacy-policy");
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", "https://ozmoveco.com.au/privacy-policy");

    return () => {
      document.title = prevTitle;
      document.querySelector('meta[name="description"]')?.setAttribute("content", prevDesc);
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", prevOgTitle);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", prevOgDesc);
      document.querySelector('meta[property="og:url"]')?.setAttribute("content", prevOgUrl);
      document.querySelector('link[rel="canonical"]')?.setAttribute("href", prevCanonical);
    };
  }, []);

  const lastUpdated = "29 July 2026";

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f7]">
      {/* Header */}
      <header className="bg-[#FFC300] sticky top-0 z-50">
        <div className="container flex flex-nowrap items-center justify-between gap-4 py-3 whitespace-nowrap">
          <a href="/" className="shrink-0">
            <img
              src={logo}
              alt="OZ MOVE CO. Logo"
              className="h-14 md:h-20 w-auto"
              width="288"
              height="192"
            />
          </a>
          <div className="flex flex-nowrap items-center gap-2 md:gap-3 shrink-0">
            <a
              href="tel:1800556654"
              aria-label="Call 1800 556 654"
              className="inline-flex items-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-3 md:px-4 py-2 font-bold text-sm rounded-full hover:bg-[#1a1a1a]/5 transition-colors whitespace-nowrap shrink-0"
            >
              <Phone className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">1800 556 654</span>
            </a>
            <a
              href="/#quote"
              className="inline-flex bg-[#1a1a1a] text-[#FFC300] px-4 md:px-5 py-2 font-bold text-sm rounded-full hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
            >
              Book Now
            </a>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <section className="py-12 md:py-20">
          <div className="container max-w-3xl">
            <div className="bg-white rounded-xl p-8 md:p-12 shadow-sm border border-[#1a1a1a]/5">
              <p className="text-[#1a1a1a]/60 text-sm mb-4">
                This page is maintained by OZ MOVE CO. to answer common privacy questions about our moving and removals services.
              </p>
              <h1 className="text-3xl md:text-4xl font-black text-[#1a1a1a] mb-2 leading-tight">
                Privacy Policy
              </h1>
              <p className="text-[#1a1a1a]/60 text-sm mb-10">
                Last updated: {lastUpdated}
              </p>

              <div className="prose prose-sm max-w-none text-[#1a1a1a]/80 leading-relaxed">
                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Introduction</h2>
                <p className="mb-4">
                  OZ MOVE CO. is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and disclose personal information when you use our website or moving services.
                </p>
                <p className="mb-4">
                  We handle personal information in line with the <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles (APPs).
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">What personal information we collect</h2>
                <p className="mb-3">We may collect the following information:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Name</li>
                  <li>Phone number</li>
                  <li>Email address</li>
                  <li>Pickup and delivery addresses</li>
                  <li>Property access details (for example, stairs, lifts, or parking restrictions)</li>
                  <li>Moving inventory information (items to be moved, special requirements)</li>
                  <li>Payment information, processed securely through third party payment providers. We do not store full card details on our own servers.</li>
                  <li>Website usage information (pages visited, device type, browser information)</li>
                </ul>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">How we collect information</h2>
                <p className="mb-3">We collect personal information when you:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Fill out a quote or booking form on our website</li>
                  <li>Call or message us</li>
                  <li>Email us</li>
                  <li>Browse or interact with our website</li>
                  <li>Accept cookies or analytics tracking</li>
                  <li>Interact with us through social media</li>
                </ul>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Why we collect information</h2>
                <p className="mb-3">We use your information to:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Provide accurate moving quotes</li>
                  <li>Confirm and manage bookings</li>
                  <li>Provide customer support before, during, and after your move</li>
                  <li>Improve our services and website experience</li>
                  <li>Send marketing communications, only where you have consented</li>
                  <li>Meet our legal and regulatory obligations</li>
                </ul>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Cookies and website analytics</h2>
                <p className="mb-4">
                  Our website uses cookies and similar technologies to understand how visitors use our site. We may use tools such as Google Analytics to collect anonymous usage data. You can manage or disable cookies through your browser settings.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Third party services we may use</h2>
                <p className="mb-3">We rely on trusted third parties to help run our business, including:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Google Analytics and Google Ads (website analytics and advertising)</li>
                  <li>Meta (social media and advertising)</li>
                  <li>Stripe, Square, or other payment processors (secure payment handling)</li>
                  <li>Customer relationship management (CRM) systems</li>
                </ul>
                <p className="mb-4">
                  These providers have their own privacy policies and handle data according to their terms. We choose reputable providers and only share the information they need to perform their services.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">How customer information is protected</h2>
                <p className="mb-4">
                  We take reasonable steps to protect your personal information from unauthorised access, loss, misuse, or disclosure. This includes secure storage, access controls, and using reputable service providers. While we take these precautions, no online system can be guaranteed to be completely secure.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Data retention</h2>
                <p className="mb-4">
                  We keep personal information only for as long as it is needed for the purposes outlined in this policy, or as required by law. When no longer needed, we take reasonable steps to securely delete or anonymise it.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">When information may be disclosed</h2>
                <p className="mb-3">We may disclose personal information:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>To contractors or team members directly involved in completing your move</li>
                  <li>To payment providers for processing transactions</li>
                  <li>When required or authorised by law, such as under a court order or regulatory request</li>
                  <li>To professional advisers or insurers where reasonably necessary</li>
                </ul>
                <p className="mb-4">We do not sell your personal information to third parties.</p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Customer rights</h2>
                <p className="mb-3">Under the Australian Privacy Principles, you have the right to:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Access the personal information we hold about you</li>
                  <li>Request corrections if your information is inaccurate or out of date</li>
                  <li>Request deletion of your information in certain circumstances</li>
                  <li>Opt out of marketing communications at any time</li>
                </ul>
                <p className="mb-4">To exercise these rights, please contact us using the details below.</p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Children's privacy</h2>
                <p className="mb-4">
                  Our services are not directed to children under 16. We do not knowingly collect personal information from children under 16. If you believe we have collected such information, please contact us so we can delete it.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Overseas data storage</h2>
                <p className="mb-4">
                  Some of the third party services we use may store or process data outside Australia. By using our services, you acknowledge that your information may be handled by overseas providers in accordance with their privacy policies and applicable laws.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Changes to this policy</h2>
                <p className="mb-4">
                  We may update this Privacy Policy from time to time. The latest version will be published on this page with the updated date. We encourage you to review it periodically.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">Contact information</h2>
                <p className="mb-4">
                  If you have any questions, concerns, or complaints about this Privacy Policy or how we handle your personal information, please contact us.
                </p>
                <div className="bg-[#f7f7f7] rounded-lg p-6 space-y-3 not-prose">
                  <a
                    href="mailto:privacy@ozmoveco.com.au"
                    className="flex items-center gap-3 text-[#1a1a1a] font-semibold hover:text-[#1a1a1a]/70 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-[#1a1a1a]/60" />
                    privacy@ozmoveco.com.au
                  </a>
                  <a
                    href="tel:1800556654"
                    className="flex items-center gap-3 text-[#1a1a1a] font-semibold hover:text-[#1a1a1a]/70 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-[#1a1a1a]/60" />
                    1800 556 654
                  </a>
                  <div className="flex items-start gap-3 text-[#1a1a1a] font-semibold">
                    <MapPin className="w-5 h-5 text-[#1a1a1a]/60 shrink-0 mt-0.5" />
                    <span>34 King William Street, Broadmeadows VIC 3047, Australia</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
